import clsx from 'clsx';
import { type JSX, memo } from 'react';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { CodeSelectionPopup } from 'ts/commons/code/CodeSelectionPopup';
import { CodeToken } from 'ts/commons/code/CodeToken';
import { CoverageMode } from 'ts/commons/code/CoverageModeSelector';
import { DataflowTraces } from 'ts/commons/code/DataflowTraces';
import type { FindingWithLayoutInfo } from 'ts/commons/code/FindingsLayout';
import {
	FINDING_MARKER_AND_GAP_WIDTH,
	FINDING_MARKER_RIGHT_PADDING,
	FindingsPopup
} from 'ts/commons/code/FindingsPopup';
import { highlightCodeSearch } from 'ts/commons/code/InCodeSearchUtils';
import { McDcCoverageBlockLine } from 'ts/commons/code/McDcCoverageBlockLine';
import { MethodInfoPopup } from 'ts/commons/code/MethodInfoPopup';
import { type LineStatus } from 'ts/commons/code/RenderedCode';
import { useRenderedCodeOptions } from 'ts/commons/code/RenderedCode.context';
import { isCodeLineSegment, type LineSegment } from 'ts/commons/code/RenderedCode.segments';
import { TestInfoPopup } from 'ts/commons/code/TestInfoPopup';
import { DateUtils } from 'ts/commons/DateUtils';
import { Links } from 'ts/commons/links/Links';
import { ObjectUtils } from 'ts/commons/ObjectUtils';
import { Popup } from 'ts/components/Popup';
import type { DecisionProbe } from 'typedefs/DecisionProbe';
import type { ELineCoverageEntry } from 'typedefs/ELineCoverage';
import type { LineBasedChangeRegion } from 'typedefs/LineBasedChangeRegion';
import type { LineBasedMethodInfo } from 'typedefs/LineBasedMethodInfo';
import type { SimpleStatementCoverageProbe } from 'typedefs/SimpleStatementCoverageProbe';
import { useStore } from 'zustand';
import { CodeLineSelection } from './CodeLineSelection';
import styles from './RenderedCode.module.less';

type CodeLineContainerProps = {
	lineNumber: number;
	lineNumberWidthInEm: number;
	findingsInLine: FindingWithLayoutInfo[];
	methodStartingHere?: LineBasedMethodInfo;
	lineCoverageState?: ELineCoverageEntry;
	lineStatus?: LineStatus;
};

/**
 * A single code line (including line number and annotations like finding bars). Internally, this renders a <tr>
 * element.
 */
function CodeLineContainer({
	lineNumber,
	lineNumberWidthInEm,
	findingsInLine,
	methodStartingHere,
	lineCoverageState,
	lineStatus
}: CodeLineContainerProps): JSX.Element {
	const {
		probeCoverageContainer,
		linesWithTokens,
		commit,
		project,
		testPathsByLineNumber,
		anyTestExecutionsExist,
		diffLineChangeTypes,
		codeChangeRegionsByLine,
		hideContextMenu,
		highlightedLinesStore,
		highlightedRefactoringSuggestion,
		dataflowInfosByLine,
		maxColumnsOnAnyLine,
		coverageSettings
	} = useRenderedCodeOptions();
	const coverageMode = coverageSettings.coverageMode;

	const selectedLines = useStore(highlightedLinesStore, state => state.selectedLines);
	const isFlashHighlightLine = useStore(highlightedLinesStore, state => state.flashHighlightedLine === lineNumber);

	// Selected means that the line is part of the selected code snippet
	let isSelected = false;
	let isFirstSelectedLine = false;

	const testPathWrapper = testPathsByLineNumber.get(lineNumber);

	if (selectedLines) {
		isSelected = lineNumber >= selectedLines.firstLine && lineNumber <= selectedLines.lastLine;
		isFirstSelectedLine = isSelected && lineNumber === selectedLines.firstLine;
	}

	const probeCoverageForLine = probeCoverageContainer.probesByLine.get(lineNumber) ?? undefined;

	function handleClickOnLineNumber() {
		const hasExistingSelectedLinesWithMoreThan1Line =
			selectedLines && selectedLines.firstLine !== selectedLines.lastLine;
		if (!selectedLines || hasExistingSelectedLinesWithMoreThan1Line) {
			highlightedLinesStore.setState({ selectedLines: new CodeLineSelection(lineNumber) });
			return;
		}
		// Check in which direction we need to expand the current code selection (upwards or downwards)
		if (lineNumber > selectedLines.firstLine) {
			highlightedLinesStore.setState({
				selectedLines: new CodeLineSelection(selectedLines.firstLine, lineNumber)
			});
		} else {
			highlightedLinesStore.setState({
				selectedLines: new CodeLineSelection(lineNumber, selectedLines.lastLine)
			});
		}
	}
	const changeRegion = codeChangeRegionsByLine.get(lineNumber);

	const isRefactoringSuggestion =
		highlightedRefactoringSuggestion != null &&
		highlightedRefactoringSuggestion.startLine <= lineNumber &&
		highlightedRefactoringSuggestion.endLine >= lineNumber;

	const dataflowInfos = dataflowInfosByLine.get(lineNumber);

	// Calculate fixed width for the findings column based on the maximum columns
	const findingsColumnWidthInPx = maxColumnsOnAnyLine * FINDING_MARKER_AND_GAP_WIDTH + FINDING_MARKER_RIGHT_PADDING;

	return (
		<>
			<tr
				className={clsx(
					'line [&>td]:py-0 leading-tight',
					lineCoverageState?.toLowerCase(),
					diffLineChangeTypes.get(lineNumber),
					{
						[styles.testGap!]: coverageMode === CoverageMode.TEST_GAP,
						[styles.testGapStart!]: coverageMode === CoverageMode.TEST_GAP && lineStatus?.isMethodStart,
						[styles.testGapEnd!]: coverageMode === CoverageMode.TEST_GAP && lineStatus?.isMethodEnd,
						[lineCoverageState?.toLowerCase() || '']:
							coverageMode === CoverageMode.TEST_COVERAGE && lineCoverageState,
						[lineStatus?.testGapStatus?.toLowerCase() || '']:
							coverageMode === CoverageMode.TEST_GAP && lineStatus?.testGapStatus,
						[styles.codeLineMarked!]: isSelected,
						[styles.codeLineMarkedRefactoring!]: isRefactoringSuggestion,
						flash: isFlashHighlightLine,
						'code-statement-path-highlight': dataflowInfos != null,
						source: dataflowInfos?.isSource,
						sink: dataflowInfos?.isSink
					}
				)}
				data-marked={isSelected}
				data-line={lineNumber}
			>
				<td
					className={clsx(styles.lineNumber, {
						[styles.lineMenuButton!]: !hideContextMenu,
						relative: isFirstSelectedLine && selectedLines
					})}
					style={{
						width: lineNumberWidthInEm + 'em',
						minWidth: lineNumberWidthInEm + 'em',
						maxWidth: lineNumberWidthInEm + 'em'
					}}
					onClick={hideContextMenu ? undefined : handleClickOnLineNumber}
				>
					{isFirstSelectedLine && selectedLines && !hideContextMenu ? <CodeSelectionPopup /> : null}
					{lineNumber}
				</td>

				{coverageMode === CoverageMode.TEST_COVERAGE && probeCoverageContainer.columnWidthInEm > 0 ? (
					<AdditionalMcDcColumns
						probeCoverageForLine={probeCoverageForLine}
						key={'mcdc-coverage-' + lineNumber}
					/>
				) : null}
				<td
					className={clsx('align-baseline min-w-[1.5em] w-[1.5em] text-right', {
						relative: changeRegion !== undefined
					})}
				>
					{methodStartingHere ? <MethodInfoPopup method={methodStartingHere} /> : null}
					{changeRegion ? <ChangeRegionTooltip changeRegion={changeRegion} /> : null}
				</td>
				<td className={anyTestExecutionsExist ? 'min-w-[1.5em] w-[1.5em]' : 'w-0'}>
					{testPathWrapper ? (
						<TestInfoPopup project={project} commit={commit} testPathWrapper={testPathWrapper} />
					) : null}
				</td>
				<td
					className={clsx({
						relative: findingsInLine.length > 0
					})}
					style={{
						width: findingsColumnWidthInPx,
						minWidth: findingsColumnWidthInPx,
						height: '1lh'
					}}
				>
					<FindingsPopup
						findings={findingsInLine}
						project={project}
						commit={commit}
						lineNumber={lineNumber}
					/>
				</td>
				<td>
					<CodeLine
						key={'line-' + lineNumber}
						segmentsOfLine={linesWithTokens.get(lineNumber)!}
						lineNumber={lineNumber}
					/>
				</td>
			</tr>
			{coverageMode === CoverageMode.TEST_COVERAGE &&
			probeCoverageContainer.columnWidthInEm > 0 &&
			probeCoverageForLine &&
			'configurations' in probeCoverageForLine ? (
				<McDcCoverageBlockLine
					lineNumber={lineNumber}
					probe={probeCoverageForLine}
					lineNumberWidthInEm={lineNumberWidthInEm}
					probeCoverageColumnWidth={probeCoverageContainer.columnWidthInEm}
				/>
			) : null}
			{dataflowInfos?.traces == null ? null : <DataflowTraces traces={dataflowInfos.traces} />}
		</>
	);
}

const MemoCodeLineContainer = memo(CodeLineContainer, ObjectUtils.deepEqual);
export { MemoCodeLineContainer as CodeLineContainer };

type AdditionMcDcColumnsProps = {
	probeCoverageForLine?: DecisionProbe | SimpleStatementCoverageProbe;
};

/**
 * Additional columns for the main code component (which is a table). NOTE: If any MC/DC coverage exists for the current
 * file, we need to include these columns for _each_ table row in order to get a well-formatted table.
 */
function AdditionalMcDcColumns({ probeCoverageForLine }: AdditionMcDcColumnsProps): JSX.Element {
	const { probeCoverageContainer } = useRenderedCodeOptions();
	return (
		<>
			<td
				className="text-right pl-2"
				style={{
					width: probeCoverageContainer.columnWidthInEm + 'em'
				}}
			>
				{probeCoverageForLine && 'trueExecutionCount' in probeCoverageForLine ? (
					<span
						className={clsx('probe-annotation', {
							'text-green-700': probeCoverageForLine.trueExecutionCount > 0,
							'text-red-700': probeCoverageForLine.trueExecutionCount === 0
						})}
					>
						{probeCoverageForLine.trueExecutionCount}
					</span>
				) : null}
				{probeCoverageForLine && 'executionCount' in probeCoverageForLine ? (
					<span
						className={clsx({
							'text-green-700': probeCoverageForLine.executionCount > 0,
							'text-red-700': probeCoverageForLine.executionCount === 0
						})}
					>
						{probeCoverageForLine.executionCount}
					</span>
				) : null}
			</td>
			<td
				className="pr-2 text-right"
				style={{
					width: probeCoverageContainer.columnWidthInEm + 'em'
				}}
			>
				{probeCoverageForLine && 'falseExecutionCount' in probeCoverageForLine ? (
					<span
						className={clsx('probe-annotation', {
							'text-green-700': probeCoverageForLine.falseExecutionCount > 0,
							'text-red-700': probeCoverageForLine.falseExecutionCount === 0
						})}
					>
						{probeCoverageForLine.falseExecutionCount}
					</span>
				) : null}
			</td>
		</>
	);
}

function ChangeRegionTooltip({ changeRegion }: { changeRegion: LineBasedChangeRegion }): JSX.Element {
	const { project, commit } = useRenderedCodeOptions();
	return (
		<Popup
			hoverable
			position="right center"
			className="max-w-[600px]"
			trigger={<span className="absolute top-0 bottom-0 !w-[2px] bg-[#d5a976]" />}
		>
			Last change to this region:{' '}
			<TeamscaleLink to={Links.commitDetails(project, commit)}>
				{DateUtils.formatTimestamp(changeRegion.lastChangeTimestamp)}
			</TeamscaleLink>
		</Popup>
	);
}

type LineTokensProps = {
	lineNumber: number;
	segmentsOfLine: LineSegment[];
};

/** Renders a single code line. */
export function CodeLine({ lineNumber, segmentsOfLine }: LineTokensProps): JSX.Element {
	const { wrapNonTokens, issueReferencesByFileOffset, searchResults, highlightedSearchResult } =
		useRenderedCodeOptions();

	return (
		<code>
			{segmentsOfLine.map(segment => {
				if (isCodeLineSegment(segment) || wrapNonTokens) {
					return (
						<CodeToken
							key={segment.fileAndOffsetId}
							lineNumber={lineNumber}
							segment={segment}
							issueReferences={issueReferencesByFileOffset.get(segment.offsetInFile) ?? []}
						/>
					);
				}
				return (
					<span key={segment.fileAndOffsetId} data-token-offset={segment.offsetInFile}>
						{highlightCodeSearch(segment, searchResults, highlightedSearchResult)}
					</span>
				);
			})}
		</code>
	);
}
